import React, { useState, useEffect, useMemo } from 'react';
import { isTenantNameHidden } from '../utils/tenantDisplay';

interface TreeNode {
    id: number;
    tenant_code: string;
    tenant_name: string;
    tenant_type: string;
    children: TreeNode[];
}

interface OrganizationTreeProps {
    apiFetch: (url: string, options?: any) => Promise<any>;
    onSelect: (tenantId: number) => void;
    selectedId?: number;
}

function filterTreeNodes(nodes: TreeNode[], selectedId?: number): TreeNode[] {
    const out: TreeNode[] = [];
    for (const node of nodes) {
        const hidden = isTenantNameHidden(node.tenant_name, node.tenant_code) && node.id !== selectedId;
        const children = filterTreeNodes(node.children || [], selectedId);
        if (hidden) {
            out.push(...children);
        } else {
            out.push({ ...node, children });
        }
    }
    return out;
}

const OrganizationTree: React.FC<OrganizationTreeProps> = ({ apiFetch, onSelect, selectedId }) => {
    const [tree, setTree] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});

    const displayTree = useMemo(() => filterTreeNodes(tree, selectedId), [tree, selectedId]);

    useEffect(() => {
        loadTree();
    }, []);

    const loadTree = async () => {
        setLoading(true);
        try {
            // 管理者用の全ツリー取得。一般ユーザー向けにはAccessibleTenantsから構築するロジックが必要だが、
            // 今回は一旦管理・レポーティング用として実装
            const data = await apiFetch('/api/admin/tenants/tree');
            setTree(data);

            // デフォルトでルートを展開
            const initialExpanded: Record<number, boolean> = {};
            data.forEach((node: TreeNode) => {
                initialExpanded[node.id] = true;
            });
            setExpanded(initialExpanded);
        } catch (err: any) {
            setError('組織構造の取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (id: number) => {
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const renderNode = (node: TreeNode, depth: number = 0) => {
        const isExpanded = expanded[node.id];
        const isSelected = selectedId === node.id;
        const hasChildren = node.children && node.children.length > 0;

        const typeIcons: Record<string, string> = {
            'CORPORATION': '🏢',
            'HEADQUARTERS': '🏛️',
            'FACILITY': '🏠'
        };

        const typeLabels: Record<string, string> = {
            'CORPORATION': '法人',
            'HEADQUARTERS': '本部',
            'FACILITY': '施設'
        };

        return (
            <div key={node.id} style={{ marginLeft: depth * 20 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                        border: isSelected ? '1px solid #2196f3' : '1px solid transparent',
                        marginBottom: '2px',
                        transition: 'all 0.2s'
                    }}
                    onClick={() => onSelect(node.id)}
                    onMouseOver={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseOut={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    {hasChildren ? (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(node.id);
                            }}
                            style={{ width: '20px', display: 'inline-block', textAlign: 'center' }}
                        >
                            {isExpanded ? '▼' : '▶'}
                        </span>
                    ) : (
                        <span style={{ width: '20px' }} />
                    )}
                    <span style={{ marginRight: '8px' }}>{typeIcons[node.tenant_type] || '📄'}</span>
                    <span style={{ flex: 1, fontWeight: isSelected ? 'bold' : 'normal' }}>
                        {node.tenant_name}
                    </span>
                    <span style={{
                        fontSize: '11px',
                        color: '#666',
                        backgroundColor: '#eee',
                        padding: '2px 6px',
                        borderRadius: '10px'
                    }}>
                        {typeLabels[node.tenant_type] || node.tenant_type}
                    </span>
                </div>
                {hasChildren && isExpanded && (
                    <div style={{ marginTop: '2px' }}>
                        {node.children.map(child => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <div>組織図読み込み中...</div>;
    if (error) return <div style={{ color: 'red' }}>{error}</div>;

    return (
        <div style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            maxHeight: '500px',
            overflowY: 'auto'
        }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', borderBottom: '2px solid #2196f3', paddingBottom: '8px' }}>
                組織階層選択
            </h3>
            {displayTree.map(root => renderNode(root))}
        </div>
    );
};

export default OrganizationTree;
